/**
 * Public /docs portal: setup wizard + MCP config generator (PR B10).
 * Feature flag docs.portal gates SPA nav; page remains reachable for operators.
 */

import { mcpSetupConfig } from "./config.js";
import { clear, copyText, el } from "./dom.js";
import {
  DEFAULT_BRIDGE_RELEASE_URL,
  claudeCodeBridgeSnippet,
  claudeCodeOauthSnippet,
  claudeDesktopOauthSnippet,
  claudeDesktopPatSnippet,
  docsWizardSections,
  type DocsSetupConfig,
} from "./docs-config.js";
import { DEFAULT_FEATURE_FLAGS, type FeatureFlags } from "./types.js";
import * as api from "./api.js";

function resolveDocsConfig(): DocsSetupConfig {
  const base = mcpSetupConfig();
  return {
    publicOrigin: base.publicOrigin,
    mcpResource: base.mcpResource,
    prmUrl: base.prmUrl,
    mcpClientId: base.mcpClientId,
    mcpScope: base.mcpScope,
    cognitoIssuer: base.cognitoIssuer,
    bridgeReleaseUrl:
      (import.meta.env.VITE_BRIDGE_RELEASE_URL as string | undefined)?.trim() ||
      DEFAULT_BRIDGE_RELEASE_URL,
  };
}

export function isDocsPath(pathname: string = window.location.pathname): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/docs" || p.startsWith("/docs/");
}

export function mountDocsPortal(container: HTMLElement): void {
  void bootDocs(container);
}

async function bootDocs(root: HTMLElement): Promise<void> {
  let features: FeatureFlags = { ...DEFAULT_FEATURE_FLAGS };
  try {
    features = await api.tryGetFeatures();
  } catch {
    // Public page — features optional
  }
  renderDocs(root, features);
}

function renderDocs(root: HTMLElement, features: FeatureFlags): void {
  clear(root);
  const cfg = resolveDocsConfig();
  const portalOn = features["docs.portal"] === true;

  const shell = el("div", { className: "app-shell docs-shell" });
  const header = el("header", { className: "topbar docs-topbar" });
  header.append(
    el(
      "div",
      { className: "brand" },
      el("img", {
        className: "logo-mark",
        src: "/logo.svg",
        alt: "",
        width: "28",
        height: "28",
      }),
      el("span", null, "MCP to Email"),
      el("span", { className: "brand-sub" }, "docs"),
    ),
    el(
      "nav",
      { className: "docs-nav" },
      el("a", { href: "/", className: "btn-ghost btn-sm" }, "App"),
      el("a", { href: "/privacy", className: "btn-ghost btn-sm" }, "Privacy"),
      el("a", { href: "/terms", className: "btn-ghost btn-sm" }, "Terms"),
    ),
  );
  shell.append(header);

  if (!portalOn) {
    shell.append(
      el(
        "div",
        { className: "alert alert-info", role: "status" },
        "Feature flag docs.portal is off — this page is available for operators. Flip the flag to advertise Setup docs in the app nav.",
      ),
    );
  }

  shell.append(
    el(
      "section",
      { className: "panel docs-hero" },
      el("h1", null, "Setup wizard"),
      el(
        "p",
        { className: "sub" },
        "Connect MCP clients to MCP to Email at mcpto.email. OAuth discovery is preferred; PAT and the stdio bridge are fallbacks.",
      ),
    ),
  );

  for (const section of docsWizardSections(cfg)) {
    shell.append(wizardSection(section.title, section.body));
  }

  shell.append(configGeneratorPanel(cfg));
  shell.append(relatedLinks());
  root.append(shell);
}

function wizardSection(title: string, body: string[]): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(el("h2", null, title));
  const ul = el("ul", { className: "docs-list" });
  for (const line of body) {
    ul.append(el("li", null, line));
  }
  panel.append(ul);
  return panel;
}

function configGeneratorPanel(cfg: DocsSetupConfig): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(
    el("h2", null, "MCP config generator"),
    el(
      "p",
      { className: "sub" },
      "Copy Claude Desktop / Code snippets. Replace email_pat_REPLACE_ME with a token minted in the app (Tokens panel).",
    ),
  );

  const snippets: Array<{ label: string; value: string; lang: string }> = [
    {
      label: "Claude Desktop — OAuth discovery (preferred)",
      value: claudeDesktopOauthSnippet(cfg),
      lang: "json",
    },
    {
      label: "Claude Desktop — PAT headers (fallback)",
      value: claudeDesktopPatSnippet(cfg),
      lang: "json",
    },
    {
      label: "Claude Code — remote HTTP (OAuth)",
      value: claudeCodeOauthSnippet(cfg),
      lang: "bash",
    },
    {
      label: "Claude Code — stdio bridge (PAT)",
      value: claudeCodeBridgeSnippet(cfg),
      lang: "bash",
    },
  ];

  for (const s of snippets) {
    panel.append(snippetBlock(s.label, s.value));
  }

  panel.append(
    el(
      "p",
      { className: "hint" },
      `Published client_id: ${cfg.mcpClientId || "(unset at build)"} · Scope: ${cfg.mcpScope} · PRM: ${cfg.prmUrl}`,
    ),
  );
  return panel;
}

function snippetBlock(label: string, value: string): HTMLElement {
  const wrap = el("div", { className: "snippet-block" });
  const head = el("div", { className: "snippet-head" });
  head.append(el("strong", null, label));
  const copyBtn = el(
    "button",
    { type: "button", className: "btn-secondary btn-sm" },
    "Copy",
  );
  copyBtn.addEventListener("click", async () => {
    const ok = await copyText(value);
    copyBtn.textContent = ok ? "Copied" : "Copy failed";
    setTimeout(() => {
      copyBtn.textContent = "Copy";
    }, 1500);
  });
  head.append(copyBtn);
  wrap.append(head);
  wrap.append(el("pre", { className: "snippet-pre" }, value));
  return wrap;
}

function relatedLinks(): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(el("h2", null, "Related docs"));
  const ul = el("ul", { className: "docs-list" });
  for (const [href, label] of [
    ["https://mcpto.email/", "Control plane app"],
    [DEFAULT_BRIDGE_RELEASE_URL, "Bridge GitLab releases"],
  ] as const) {
    ul.append(
      el(
        "li",
        null,
        el("a", { href, target: "_blank", rel: "noopener noreferrer" }, label),
      ),
    );
  }
  panel.append(ul);
  panel.append(
    el(
      "p",
      { className: "hint" },
      "Repo markdown: docs/mcp-clients.md, docs/invite-runbook.md (Google Testing), docs/cf-mcp-smoke.md.",
    ),
  );
  return panel;
}
